/**
 * Item Type Icons
 * Maps content types to their FontAwesome icons and colors
 * Based on online-community/Assets/projects/ocfe/src/utils/itemTypeIcons.ts
 */

import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faClock } from '@fortawesome/pro-light-svg-icons';
import {
  faAlignJustify,
  faBadgeCheck,
  faBook,
  faBriefcase,
  faBrowser,
  faCalendarAlt,
  faChalkboardTeacher,
  faCircleNotch,
  faComment,
  faCommentQuestion,
  faFont,
  faHeart,
  faLightbulb,
  faMegaphone,
  faNewspaper,
} from '@fortawesome/pro-solid-svg-icons';

export type ContentType =
  | 'Announcement'
  | 'Blog'
  | 'Community'
  | 'Course'
  | 'CalendarEvent'
  | 'Glossary'
  | 'Ideation'
  | 'LibraryEntry'
  | 'MessagePost'
  | 'MessageThread'
  | 'Page'
  | 'Question'
  | 'Answer'
  | 'QAndA'
  | 'VolunteerOpportunity'
  | 'CalendarDate'
  | 'ExternalJob'
  | 'DocumentFile'
  | 'Document'
  | 'AutomatedResponseRef';

export interface ContentTypeConfig {
  icon: IconDefinition;
  color: string;
  display: string;
}

/**
 * Get icon, color, and display label for a given content type
 * Matches the implementation from online-community
 */
export const getIconForItemType = (type: string): ContentTypeConfig => {
  switch (type) {
    case 'Announcement':
      return { icon: faMegaphone, color: '#61918D', display: 'Announcement' };
    
    case 'Blog':
      return { icon: faNewspaper, color: '#17a2b8', display: 'Blog' };
    
    case 'Community':
      return { icon: faCircleNotch, color: '#F58026', display: 'Community' };
    
    case 'Course':
      return { icon: faChalkboardTeacher, color: '#1F7CEF', display: 'Course/Webinar' };
    
    case 'CalendarEvent':
      return { icon: faCalendarAlt, color: '#D0764F', display: 'Event' };
    
    case 'Glossary':
      return { icon: faFont, color: '#9D0F7E', display: 'Glossary' };
    
    case 'Ideation':
      return { icon: faLightbulb, color: '#EFBB56', display: 'Idea' };
    
    case 'LibraryEntry':
    case 'Document':
      return { icon: faBook, color: '#6c757d', display: 'Library Entry' };
    
    case 'MessagePost':
    case 'MessageThread':
      return { icon: faComment, color: '#007bff', display: 'Discussion' };
    
    case 'Page':
      return { icon: faBrowser, color: '#84CDA1', display: 'Web Page' };
    
    case 'Question':
    case 'Answer':
    case 'QAndA':
      return { icon: faCommentQuestion, color: '#EFBB56', display: 'Question' };
    
    case 'VolunteerOpportunity':
      return { icon: faHeart, color: '#CB3767', display: 'Volunteer Opportunity' };
    
    case 'CalendarDate':
      return { icon: faClock, color: '#D0764F', display: 'Calendar Date' };
    
    case 'ExternalJob':
      return { icon: faBriefcase, color: '#61918D', display: 'Job Opportunity' };
    
    case 'AutomatedResponseRef':
      return { icon: faBadgeCheck, color: '#FFAE0D', display: 'Official Answer' };
    
    case 'DocumentFile':
      return { icon: faBook, color: '#6c757d', display: 'Document' };
    
    default:
      return { icon: faAlignJustify, color: '#C4C4C4', display: 'Other' };
  }
};

